import React, { useEffect, useMemo, useRef } from 'react';

import PropTypes from 'prop-types';
import { Field, useForm, useFormState } from 'react-final-form';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';

import { FormCustomSelect, FormPillSelector } from 'components';
import { actions, duck } from 'coupon-common/src/modules/businessCategories';
import { atLeastOneSelected, getNormalizedEntity, required } from 'coupon-common/src/utils';

import messages from './messages';

const MAX_SUBCATEGORIES = 5;

export const CouponCategoryField = ({ intl, dispatch, businessCategories }) => {
  useEffect(() => {
    // ✅ Safe guard: handle undefined/null entities before calling Object.keys
    const entities = businessCategories?.entities?.businessCategories;
    const isEmpty = !entities || Object.keys(entities).length === 0;
    if (isEmpty) {
      dispatch(actions.getBusinessCategories());
    }
  }, [dispatch]);

  const form = useForm();
  const businessCategoriesKey = duck.businessCategoriesEntity.key;

  const {
    entityList: businessCategoriesOptions,
    isFetching: businessCategoriesFetching,
    error: businessCategoriesLoadingError,
  } = getNormalizedEntity(
    businessCategories,
    businessCategoriesKey,
    intl,
    messages.entityCouponCategories,
  );

  const { values: { category = {} } = {} } = useFormState({
    subscription: { values: true },
  });

  // FormCustomSelect expects { label, value } — normalize from { id, name }
  const normalizedOptions = useMemo(
    () => businessCategoriesOptions.map(c => ({ ...c, label: c.label || c.name, value: c.value ?? c.id })),
    [businessCategoriesOptions],
  );

  const selectedCategoryId = category?.id;

  let selectedCategory;
  const subcategoryOptions = useMemo(() => {
    selectedCategory = normalizedOptions.find(c => c.id === selectedCategoryId);
    return selectedCategory?.subcategories || [];
  }, [selectedCategoryId, normalizedOptions]);

  const prevCategoryIdRef = useRef();

  useEffect(() => {
    const prevCategoryId = prevCategoryIdRef.current;
    if (prevCategoryId && prevCategoryId !== selectedCategoryId) {
      form.change('subcategories', undefined);
    }
    prevCategoryIdRef.current = selectedCategoryId;
  }, [selectedCategoryId]);

  return (
    <div>
      {/* Category pills */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
        {normalizedOptions.map(opt => {
          const isSelected = category?.id === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => form.change('category', isSelected ? undefined : opt)}
              style={{
                padding: '9px 20px', borderRadius: 999,
                border: isSelected ? 'none' : '1.5px solid #d1d5db',
                background: isSelected ? '#1a1a1a' : '#fff',
                color: isSelected ? '#fff' : '#374151',
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
                transition: 'background .15s',
              }}
            >
              {opt.name}
            </button>
          );
        })}
      </div>

      {selectedCategory && subcategoryOptions?.length > 0 && (
        <>
          <h6 style={{ margin: '12px 0 6px', fontSize: 13, fontWeight: 700, color: '#374151' }}>
            <FormattedMessage {...messages.subcatBlockHeading} />
          </h6>
          <p style={{ fontSize: 12, color: '#9ca3af', margin: '0 0 10px' }}>
            <FormattedMessage {...messages.subcatInstruction} />
          </p>
          <Field name="subcategories" validate={(value, _, meta) => meta?.touched ? required(value) : undefined}>
            {({ input }) => {
              const selected = input.value || [];
              const maxReached = selected.length >= MAX_SUBCATEGORIES;
              return (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  {subcategoryOptions.map(sub => {
                    const isSelected = selected.includes(sub.id ?? sub.value ?? sub);
                    const isDisabled = !isSelected && maxReached;
                    return (
                      <button
                        key={sub.id ?? sub.value ?? sub}
                        type="button"
                        disabled={isDisabled}
                        onClick={() => {
                          const id = sub.id ?? sub.value ?? sub;
                          if (isSelected) {
                            input.onChange(selected.filter(s => s !== id));
                            return;
                          }
                          if (selected.length < MAX_SUBCATEGORIES) {
                            input.onChange([...selected, id]);
                          }
                        }}
                        style={{
                          padding: '9px 20px', borderRadius: 999,
                          border: isSelected ? 'none' : '1.5px solid #d1d5db',
                          background: isSelected ? '#1a1a1a' : '#fff',
                          color: isSelected ? '#fff' : '#374151',
                          fontSize: 13, fontWeight: 600,
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                          opacity: isDisabled ? 0.4 : 1,
                          transition: 'background .15s',
                        }}
                      >
                        {sub.name ?? sub.label ?? sub}
                      </button>
                    );
                  })}
                </div>
              );
            }}
          </Field>
        </>
      )}
    </div>
  );
};

CouponCategoryField.propTypes = {
  intl: PropTypes.object,
  dispatch: PropTypes.func,
  businessCategories: PropTypes.object,
};

const mapStateToProps = state => ({
  businessCategories: state.businessCategories,
});

export default connect(mapStateToProps)(injectIntl(CouponCategoryField));